(() => {
  const consentRoot = document.querySelector('[data-vdl-consent]');
  let privacyPromise;
  const privacyApi = () => {
    const api = window.Shopify?.customerPrivacy;
    return typeof api?.setTrackingConsent === 'function' ? api : null;
  };
  const loadPrivacy = () => {
    const existing = privacyApi();
    if (existing) return Promise.resolve(existing);
    if (privacyPromise) return privacyPromise;
    privacyPromise = new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error('Privacy API unavailable')), 10000);
      const done = error => {
        window.clearTimeout(timeout);
        const api = privacyApi();
        if (error || !api) reject(new Error('Privacy API unavailable'));
        else resolve(api);
      };
      if (typeof window.Shopify?.loadFeatures !== 'function') return done(true);
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], done);
    }).catch(error => {
      privacyPromise = null;
      throw error;
    });
    return privacyPromise;
  };

  if (consentRoot) {
    const dialog = consentRoot.querySelector('[data-consent-dialog]');
    const banner = consentRoot.querySelector('[data-consent-banner]');
    const status = consentRoot.querySelector('[data-consent-status]');
    const result = consentRoot.querySelector('[data-consent-result]');
    const categories = [...consentRoot.querySelectorAll('[data-consent-category]')];
    const saleRow = consentRoot.querySelector('[data-consent-sale-row]');
    const saleInput = consentRoot.querySelector('[data-consent-sale-opt-out]');
    let lastFocus;
    let saving = false;
    const setEnabled = enabled => {
      for (const el of consentRoot.querySelectorAll('[data-consent-category],[data-consent-save],[data-consent-accept],[data-consent-reject],[data-consent-sale-opt-out]')) el.disabled = !enabled;
    };
    const refresh = api => {
      const current = api.currentVisitorConsent();
      categories.forEach(input => { input.checked = current[input.dataset.consentCategory] === 'yes'; });
      const saleRegion = api.saleOfDataRegion();
      saleRow.hidden = !saleRegion;
      saleInput.checked = current.sale_of_data === 'no';
      setEnabled(true);
      status.textContent = '';
    };
    const open = async () => {
      lastFocus = document.activeElement;
      result.hidden = true;
      if (typeof window.privacyBanner?.showPreferences === 'function') {
        try {
          await window.privacyBanner.showPreferences();
          banner.hidden = true;
          return;
        } catch {}
      }
      if (!dialog.open) dialog.showModal();
      status.textContent = consentRoot.dataset.loading;
      setEnabled(false);
      try { refresh(await loadPrivacy()); }
      catch { status.textContent = consentRoot.dataset.error; }
    };
    const close = () => {
      dialog.close();
      lastFocus?.focus();
    };
    const save = async mode => {
      if (saving) return;
      saving = true;
      setEnabled(false);
      try {
        const api = await loadPrivacy();
        const choice = {};
        categories.forEach(input => { choice[input.dataset.consentCategory] = mode === 'accept' || (mode === 'custom' && input.checked); });
        if (api.saleOfDataRegion() && mode === 'custom') choice.sale_of_data = !saleInput.checked;
        if (api.saleOfDataRegion() && mode === 'reject') choice.sale_of_data = false;
        await new Promise((resolve, reject) => {
          const timeout = window.setTimeout(() => reject(new Error('Consent save timed out')), 10000);
          api.setTrackingConsent(choice, () => { window.clearTimeout(timeout); resolve(); });
        });
        const current = api.currentVisitorConsent();
        const saved = categories.every(input => current[input.dataset.consentCategory] === (choice[input.dataset.consentCategory] ? 'yes' : 'no'));
        if (!saved) throw new Error('Consent was not saved');
        refresh(api);
        banner.hidden = true;
        if (dialog.open) close();
        result.textContent = consentRoot.dataset.saved;
        result.hidden = false;
        window.setTimeout(() => { result.hidden = true; }, 6000);
      } catch {
        if (!dialog.open) { lastFocus = document.activeElement; dialog.showModal(); }
        status.textContent = consentRoot.dataset.error;
        setEnabled(Boolean(privacyApi()));
      } finally { saving = false; }
    };
    document.querySelectorAll('[data-consent-open]').forEach(button => button.addEventListener('click', open));
    consentRoot.querySelector('[data-consent-close]').addEventListener('click', close);
    dialog.addEventListener('close', () => lastFocus?.focus());
    consentRoot.querySelectorAll('[data-consent-reject]').forEach(button => button.addEventListener('click', () => save('reject')));
    consentRoot.querySelectorAll('[data-consent-accept]').forEach(button => button.addEventListener('click', () => save('accept')));
    consentRoot.querySelector('[data-consent-save]').addEventListener('click', () => save('custom'));
    loadPrivacy().then(api => {
      refresh(api);
      const current = api.currentVisitorConsent();
      const noDecision = ['analytics', 'marketing', 'preferences'].every(key => !current[key]);
      const hasNativeBanner = typeof window.privacyBanner?.showPreferences === 'function';
      if (!hasNativeBanner && (api.shouldShowBanner() || noDecision)) banner.hidden = false;
    }).catch(() => { status.textContent = consentRoot.dataset.error; });
  }

  const withdrawalRoot = document.querySelector('[data-vdl-withdrawal]');
  if (!withdrawalRoot) return;
  const form = withdrawalRoot.querySelector('form');
  const review = withdrawalRoot.querySelector('[data-withdrawal-review]');
  const storageKey = 'vdl-withdrawal-pending';
  const readPending = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (!saved || Date.now() - saved.created > 7200000) { sessionStorage.removeItem(storageKey); return null; }
      return saved;
    } catch { return null; }
  };
  const values = () => ({
    name: form.elements.namedItem('contact[name]').value.trim(),
    email: form.elements.namedItem('contact[email]').value.trim(),
    order: form.elements.namedItem('contact[bestelling]').value.trim(),
    items: form.elements.namedItem('contact[producten]').value.trim() || withdrawalRoot.dataset.allItems
  });
  const copyText = data => [
    withdrawalRoot.dataset.receiptTitle,
    `${withdrawalRoot.dataset.referenceLabel}: ${data.reference}`,
    `${withdrawalRoot.dataset.dateLabel}: ${data.sentAt}`,
    '', withdrawalRoot.dataset.statement, '',
    `${withdrawalRoot.dataset.nameLabel}: ${data.name}`,
    `${withdrawalRoot.dataset.emailLabel}: ${data.email}`,
    `${withdrawalRoot.dataset.orderLabel}: ${data.order}`,
    `${withdrawalRoot.dataset.itemsLabel}: ${data.items}`,
    '', withdrawalRoot.dataset.receiptNote,
    'info@vdsieraden.nl'
  ].join('\n');

  if (withdrawalRoot.querySelector('[data-withdrawal-success]')) {
    const pending = readPending();
    if (!pending) return;
    try { sessionStorage.removeItem(storageKey); } catch {}
    const download = withdrawalRoot.querySelector('[data-withdrawal-download]');
    download.hidden = false;
    withdrawalRoot.querySelector('[data-withdrawal-no-copy]').hidden = true;
    download.addEventListener('click', () => {
      try {
        const url = URL.createObjectURL(new Blob([copyText(pending)], { type: 'text/plain;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `van-dalen-herroeping-${pending.reference}.txt`;
        document.body.append(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch { withdrawalRoot.querySelector('[data-withdrawal-copy-status]').textContent = withdrawalRoot.dataset.copyError; }
    });
    return;
  }

  let lastFocus;
  const openReview = () => {
    if (!form.reportValidity()) return;
    const data = values();
    for (const [key, value] of Object.entries(data)) withdrawalRoot.querySelector(`[data-review-${key}]`).textContent = value;
    lastFocus = document.activeElement;
    review.showModal();
  };
  withdrawalRoot.querySelector('[data-withdrawal-submit]').addEventListener('click', openReview);
  form.addEventListener('submit', event => {
    if (form.dataset.confirmed === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openReview();
  }, { capture: true });
  form.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || event.target.matches('textarea,button')) return;
    event.preventDefault();
    openReview();
  });
  withdrawalRoot.querySelector('[data-withdrawal-edit]').addEventListener('click', () => review.close());
  review.addEventListener('close', () => lastFocus?.focus());
  withdrawalRoot.querySelector('[data-withdrawal-confirm]').addEventListener('click', () => {
    if (!form.reportValidity()) { review.close(); return; }
    const now = new Date();
    const reference = 'VDL-' + now.getTime().toString(36).toUpperCase() + '-' + (window.crypto?.randomUUID?.().slice(0, 8) || Math.random().toString(36).slice(2, 10)).toUpperCase();
    const data = { ...values(), reference, sentAt: now.toISOString(), created: now.getTime() };
    form.querySelector('[data-withdrawal-body]').value = copyText(data);
    try { sessionStorage.setItem(storageKey, JSON.stringify(data)); } catch {}
    form.dataset.confirmed = 'true';
    review.close();
    form.requestSubmit();
  });
  withdrawalRoot.querySelector('[data-withdrawal-submit]').disabled = false;
  withdrawalRoot.querySelector('[data-withdrawal-loading]').hidden = true;
})();
